import React, { useCallback, useMemo } from 'react';

import {
  IonIcon,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import {
  documentTextOutline,
  informationCircleOutline,
  timeOutline,
} from 'ionicons/icons';

import { Redirect, Route } from 'react-router-dom';

import Actions, { Action } from 'components/Actions';
import ErrorContent from 'components/ErrorContent';
import Modal, {
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from 'components/Modal';
import Typography from 'components/Typography';
import { useIsIncluded } from 'hooks/useIsIncluded';
import {
  TAB_ACTIONS_LOG,
  TAB_ATTACHMENTS,
  TAB_DETAILS,
} from 'scenes/FinancePage/IO/constants';
import {
  ACTION_APPROVE,
  ACTION_MORE_INFO,
  ACTION_REJECT,
  ACTION_UNDO,
} from 'scenes/FinancePage/IO/constants';
import {
  useApprove,
  useMoreInfo,
  useReject,
  useUndo,
} from 'scenes/FinancePage/IO/data/mutations';
import { useIsUpdating } from 'scenes/FinancePage/IO/data/useIsUpdating';
import { useLoadIO } from 'scenes/FinancePage/IO/data/useLoadIO';

import ActionsLog from './components/ActionsLog';
import Attachments from './components/Attachments';
import Details from './components/Details';
import DetailsSkeleton from './components/DetailsSkeleton';
import useDocuSigning from './useDocuSigning';

const IODetailsModal = ({
  id,
  parentPath,
  onClose = () => {},
  onSubmit = () => {},
}: any) => {
  const { data, isRefetching, isLoading, isError, error, isSuccess } =
    useLoadIO({
      id,
    });
  const isUpdating = useIsUpdating();
  const { attachments, actionsLog, ...details } = data || {};

  const isActionAllowed = useIsIncluded(data?.actions);

  const [approve] = useApprove();
  const [reject] = useReject();
  const [undo] = useUndo();
  const [moreInfo] = useMoreInfo();

  const handleApprove = useCallback(() => {
    approve({ id, status: details?.status });
  }, [id, details?.status, approve]);

  const handleReject = useCallback(() => {
    reject({
      id,
      businessPartner: details?.businessPartner,
      period: details?.period,
      createdBy: details?.metadata?.createdBy,
      parentIo: details?.parentIo,
    });
  }, [
    id,
    details?.businessPartner,
    details?.period,
    details?.metadata?.createdBy,
    details?.parentIo,
    reject,
  ]);

  const handleUndo = useCallback(() => {
    undo({ id });
  }, [id, undo]);

  const handleMoreInfo = useCallback(() => {
    moreInfo({
      id,
      businessPartner: details?.businessPartner,
      period: details?.period,
      createdBy: details?.metadata?.createdBy,
      parentIo: details?.parentIo,
    });
  }, [
    id,
    details?.businessPartner,
    details?.period,
    details?.metadata?.createdBy,
    details?.parentIo,
    moreInfo,
  ]);

  const routes = useMemo(() => {
    return {
      [TAB_DETAILS]: `${parentPath}/${TAB_DETAILS}`,
      [TAB_ATTACHMENTS]: `${parentPath}/${TAB_ATTACHMENTS}`,
      [TAB_ACTIONS_LOG]: `${parentPath}/${TAB_ACTIONS_LOG}`,
    };
  }, [parentPath]);

  const renderDetailsComponent = useMemo(() => {
    switch (true) {
      case isError: //Loaded with error response
        return <ErrorContent text={error} />;
      case isSuccess: //Loaded with normal response
        return <Details data={details} />;
      case isLoading:
        return <DetailsSkeleton />; //No loaded
      default:
        return null;
    }
  }, [isLoading, isSuccess, isError, details, error]);

  useDocuSigning({ data });

  return (
    <Modal onClose={onClose} onSubmit={onSubmit}>
      <span></span>
      <IonReactRouter>
        <IonTabs>
          <IonTabBar slot={'top'}>
            <IonTabButton tab={TAB_DETAILS} href={routes[TAB_DETAILS]}>
              <Typography variant={'caption'}>Details</Typography>
              <IonIcon icon={informationCircleOutline} />
            </IonTabButton>
            <IonTabButton
              tab={TAB_ATTACHMENTS}
              href={routes[TAB_ATTACHMENTS]}
              disabled={!attachments?.length}
            >
              <Typography variant={'caption'}>
                Attachments ({attachments?.length || 0})
              </Typography>
              <IonIcon icon={documentTextOutline} />
            </IonTabButton>
            <IonTabButton tab={TAB_ACTIONS_LOG} href={routes[TAB_ACTIONS_LOG]}>
              <Typography variant={'caption'}>Action Logs</Typography>
              <IonIcon icon={timeOutline} />
            </IonTabButton>
          </IonTabBar>
          <IonRouterOutlet>
            <Redirect exact path={parentPath} to={routes[TAB_DETAILS]} />
            <Route exact path={routes[TAB_DETAILS]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                {renderDetailsComponent}
              </ModalContent>
            </Route>
            <Route exact path={routes[TAB_ATTACHMENTS]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <Attachments data={attachments} />
              </ModalContent>
            </Route>
            <Route exact path={routes[TAB_ACTIONS_LOG]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <ActionsLog data={actionsLog} />
              </ModalContent>
            </Route>
          </IonRouterOutlet>
        </IonTabs>
      </IonReactRouter>
      <ModalFooter>
        <ModalCloseButton />
        <ModalTitle>IO #{id}</ModalTitle>
        <Actions header={'More actions'} limit={1}>
          {isActionAllowed(ACTION_APPROVE) ? (
            <Action
              onClick={handleApprove}
              color={'primary'}
              label={'Approve'}
            />
          ) : null}
          {isActionAllowed(ACTION_MORE_INFO) ? (
            <Action onClick={handleMoreInfo} label={'Request more info'} />
          ) : null}
          {isActionAllowed(ACTION_UNDO) ? (
            <Action onClick={handleUndo} label={'Undo'} />
          ) : null}
          {isActionAllowed(ACTION_REJECT) ? (
            <Action onClick={handleReject} label={'Reject'} />
          ) : null}
        </Actions>
      </ModalFooter>
    </Modal>
  );
};

export default IODetailsModal;
